import { useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import type { Page } from "../../api";
import { FlagChip } from "../../chips";
import { TabPage } from "../../PageFrame";
import { FUNNEL_LABELS } from "../../SharedFilterBar";
import type { PageProps } from "../index";
import { MUTED_ROW, PAGE_SIZE, PagedTable, PageHead, sortParams, usePage, useTableSort, type TableSort } from "../kit";
import { pageExtra } from "../params";

export interface CatalogRow {
  funnel: string;
  feature: string | null;
  criterion: string;
  question: string | null;
  criteria_phase: string | null;
  default_weight: number | null;
  rubric: string | null;
  retired: boolean;
  retired_reason: string | null;
}

export const CATALOG_COLUMNS: ColumnDef<CatalogRow, unknown>[] = [
  // The catalogue is kept per funnel, not per item, so the first column names the funnel.
  { id: "funnel", header: "Funnel", meta: { sortable: true }, cell: ({ row }) => FUNNEL_LABELS[row.original.funnel] ?? row.original.funnel },
  { id: "feature", header: "Feature", meta: { sortable: true }, cell: ({ row }) => row.original.feature },
  {
    id: "criterion",
    header: "Criterion",
    meta: { sortable: true },
    cell: ({ row }) => (
      <span className="flex flex-wrap items-center gap-1">
        {row.original.criterion}
        {row.original.retired ? <FlagChip kind="retired" /> : null}
      </span>
    ),
  },
  { id: "question", header: "Question", meta: { sortable: true }, cell: ({ row }) => row.original.question },
  { id: "criteria_phase", header: "Criteria phase", meta: { sortable: true }, cell: ({ row }) => row.original.criteria_phase },
];

export function CriteriaCatalogBody({ data, page, onPage, sort = null, onSort }: {
  data: Page<CatalogRow>; page: number; onPage: (next: number) => void; sort?: TableSort; onSort?: (id: string) => void;
}) {
  return (
    <PagedTable<CatalogRow>
      columns={CATALOG_COLUMNS}
      data={data}
      page={page}
      onPage={onPage}
      caption="Criteria catalogue"
      wrap
      sort={sort}
      onSort={onSort}
      rowClassName={(row) => (row.retired ? MUTED_ROW : undefined)}
    />
  );
}

export function CriteriaCatalogPage({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const [page, setPage] = usePage();
  const [sort, onSort] = useTableSort();
  return (
    <TabPage<Page<CatalogRow>>
      tab={tab}
      areaName={areaName}
      extra={{ ...pageExtra(tab.tabKey, search), ...sortParams(sort), limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }}
      isEmpty={(data) => data.total === 0}
      head={(data) => (
        <PageHead
          title={tab.label}
          sub={data ? "The questions options are judged on" : undefined}
          kpi={data ? { label: "Total criteria", value: data.total } : null}
        />
      )}
    >
      {(data) => <CriteriaCatalogBody data={data} page={page} onPage={setPage} sort={sort} onSort={onSort} />}
    </TabPage>
  );
}
